import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import styles from './index.module.scss';

export interface NavCategoryV3Props {
  categories: {
    id: string;
    selector: string;
    label: string;
    icon: string;
  }[];
  activeSelector: string | null;
  onScrollTo: (selector: string) => void;
}

export interface NavCategoryV3Handle {
  setActiveSelector: (selector: string) => void;
}

export const NavCategoryV3 = forwardRef<
  NavCategoryV3Handle,
  NavCategoryV3Props
>(({ categories, activeSelector, onScrollTo }, ref) => {
  const navRef = useRef<HTMLDivElement>(null);
  const [isSticky, setSticky] = useState(false);
  const [currentActive, setCurrentActive] = useState<string | null>(
    activeSelector
  );

  useImperativeHandle(ref, () => ({
    setActiveSelector: (selector: string) => {
      setCurrentActive(selector);
    },
  }));

  const handleScroll = useCallback(() => {
    if (navRef.current) {
      requestAnimationFrame(() => {
        if (!navRef.current) return;
        if (window.scrollY < 300) {
          setSticky(false);
        } else {
          setSticky(navRef.current.getBoundingClientRect().top <= 85);
        }
      });
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setCurrentActive(activeSelector);
  }, [activeSelector]);

  const handleClick = (e: React.MouseEvent, selector: string) => {
    e.preventDefault();
    onScrollTo(selector);
  };

  return (
    <div
      ref={navRef}
      className={`${styles.navCategory} ${isSticky ? styles.sticky : ''}`}
    >
      <div className="container">
        <ul className={styles.navList}>
          {(categories || []).map((category) => {
            const isActive = currentActive === category.selector;
            return (
              <li key={category.id} className={styles.navItem}>
                <a
                  href={`#${category.selector}`}
                  className={`${styles.navLink} ${
                    isActive ? styles.active : ''
                  }`}
                  onClick={(e) => handleClick(e, category.selector)}
                >
                  <img src={category.icon} alt={category.label} />
                  <span className={styles.navLabel}>{category.label}</span>
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
});
